import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import EditorialMarquee from "@/components/EditorialMarquee";
import { Reveal, Overline } from "@/components/Reveal";
import { IMAGES, REAL } from "@/lib/site";

const PILLARS = [
  {
    num: "01",
    title: "Design",
    desc: "Every project begins with listening. We design around your space, your lifestyle, and your vision — balancing modern aesthetics with layouts that genuinely work for everyday life.",
  },
  {
    num: "02",
    title: "Craftsmanship",
    desc: "Cabinetry and interiors are only as good as how they're built. We obsess over joinery, materials, hardware, and finishes so the result feels as good as it looks — and lasts.",
  },
  {
    num: "03",
    title: "Functionality",
    desc: "Beautiful spaces should also be effortless to live in. Smart storage, considered layouts, and thoughtful details make every room work harder for you.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <SEO
        title="About Us — Custom Cabinetry & Interior Renovations GTA | Hive Interiors"
        description="Hive Interiors brings extensive experience in cabinetry, interior construction, renovation, design and project management to homes across the GTA."
      />
      <PageHero
        image={IMAGES.aboutHero}
        overline="About Hive Interiors"
        title="Design + Craftsmanship"
        italic="+ Functionality"
        sub="Hive Interiors brings extensive experience in cabinetry, interior construction, renovation, design, and project management to homes across the Greater Toronto Area."
        alt="Hive Interiors custom kitchen craftsmanship"
      />
      <EditorialMarquee />

      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16 md:mb-24">
            <Reveal className="md:col-span-3">
              <Overline>01 / Our Philosophy</Overline>
            </Reveal>
            <Reveal delay={0.1} className="md:col-span-9">
              <p className="font-heading text-3xl md:text-5xl tracking-tight leading-[1.2]">
                We believe a home should be{" "}
                <span className="italic text-primary">designed around the people who live in it</span>{" "}
                — not the other way around.
              </p>
              <p className="mt-8 max-w-2xl text-base text-muted-foreground leading-relaxed">
                From custom kitchen cabinets to complete home interior transformations, we combine modern design, smart functionality, and quality craftsmanship to create beautiful, customized spaces built to last. One team guides you from the first consultation to the final walkthrough.
              </p>
            </Reveal>
          </div>

          <div>
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.05}>
                <div
                  data-testid={`about-pillar-${p.num}`}
                  className="group grid grid-cols-12 gap-6 items-center py-10 md:py-14 border-t border-border last:border-b"
                >
                  <span className="col-span-3 md:col-span-2 font-heading italic text-6xl md:text-8xl text-muted/60 group-hover:text-primary transition-colors duration-500 leading-none">
                    {p.num}
                  </span>
                  <h2 className="col-span-9 md:col-span-4 font-heading text-2xl md:text-4xl tracking-tight">
                    {p.title}
                  </h2>
                  <p className="col-span-12 md:col-span-6 text-sm md:text-base text-muted-foreground leading-relaxed md:pl-8">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-20">
              <Overline>On Site</Overline>
              <h2 className="mt-5 font-heading text-3xl md:text-4xl tracking-tight">
                Real Work, <span className="italic text-primary">In Progress</span>
              </h2>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div data-testid="about-onsite-1" className="group overflow-hidden">
                  <img
                    src={REAL.installKitchen}
                    alt="Hive Interiors kitchen cabinets being installed on site"
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div data-testid="about-onsite-2" className="group overflow-hidden">
                  <img
                    src={REAL.installWardrobe}
                    alt="Hive Interiors custom wall panelling installation in progress"
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 flex justify-center">
              <Link
                to="/contact"
                data-testid="about-consultation-button"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-300 hover:bg-accent hover:text-accent-foreground group"
              >
                Start a Conversation
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
